import PATHS from "routes/paths";
import ApplicationUpdateNotifier from "./ApplicationUpdateNotifier/ApplicationUpdateNotifier";

interface UpdateNotificationState {
  ui?: {
    updateNotification?: {
      visible?: boolean;
    };
  };
}

interface ReloadStore {
  getState: () => UpdateNotificationState | null | undefined;
  dispatch: (...args: unknown[]) => unknown;
}

type ReloadWindow = Window & {
  __swReloading__?: boolean;
  __swControllerChangeListenerInstalled__?: boolean;
  __swControllerChangeReloadPending__?: boolean;
  __swReloadOnNextControllerChange__?: boolean;
  inline_manual_player?: {
    update: () => void;
  };
};

interface ServiceWorkerReloadDeps {
  store: ReloadStore;
  serviceWorker: ServiceWorkerContainer;
  window: ReloadWindow;
  dispatchNotification?: () => void;
}

interface NavigationLocation {
  pathname: string;
}

interface ServiceWorkerNavigationDeps {
  location: NavigationLocation;
  previousLocation?: NavigationLocation;
  store: ReloadStore;
  serviceWorker?: ServiceWorkerContainer;
  window: ReloadWindow;
  reloadHandler?: (deps: ServiceWorkerReloadDeps) => void;
}

interface ServiceWorkerNavigationListenerDeps {
  initialLocation: NavigationLocation;
  store: ReloadStore;
  serviceWorker?: ServiceWorkerContainer;
  window: ReloadWindow;
  reloadHandler?: (deps: ServiceWorkerReloadDeps) => void;
}

const ROUTE_PATHS = PATHS as typeof PATHS & {
  isEditorPath: (path: string) => boolean;
};

const getErrorMessage = (error: unknown) => {
  return error instanceof Error ? error.message : String(error);
};

const isEditorPath = (path: string | undefined) => {
  return Boolean(path && ROUTE_PATHS.isEditorPath(path));
};

const isDeferredReloadSafe = (
  previousPathname: string | undefined,
  nextPathname: string
) => {
  if (!previousPathname) {
    return !isEditorPath(nextPathname);
  }

  return !(isEditorPath(previousPathname) && isEditorPath(nextPathname));
};

const reloadApplication = (window: ReloadWindow) => {
  if (window.__swReloading__) return;

  window.__swReloading__ = true;
  window.location.reload();
};

/**
 * Records controller changes so an active service-worker update reloads at the
 * next safe navigation point.
 */
export function listenForServiceWorkerControllerChanges({
  serviceWorker,
  window
}: {
  serviceWorker?: ServiceWorkerContainer;
  window: ReloadWindow;
}) {
  if (!serviceWorker || window.__swControllerChangeListenerInstalled__) return;

  window.__swControllerChangeListenerInstalled__ = true;
  let hasObservedController = Boolean(serviceWorker.controller);

  serviceWorker.addEventListener("controllerchange", () => {
    if (!hasObservedController) {
      hasObservedController = true;
      if (!window.__swReloadOnNextControllerChange__) return;
    }

    if (window.__swReloadOnNextControllerChange__) {
      window.__swReloadOnNextControllerChange__ = false;
      console.info("Service worker controller changed. Reloading application");
      reloadApplication(window);
      return;
    }

    window.__swControllerChangeReloadPending__ = true;
  });
}

/**
 * Applies deferred service-worker update reloads on safe SPA navigations.
 *
 * The handler uses the service-worker registration as the update source of
 * truth, then falls back to the Redux notification flag for already-applied
 * updates that are ready for a navigation-time reload.
 */
export function handleServiceWorkerReload({
  store,
  serviceWorker,
  window,
  dispatchNotification = ApplicationUpdateNotifier.dispatchNotification
}: ServiceWorkerReloadDeps) {
  if (window.__swReloading__) return;

  listenForServiceWorkerControllerChanges({ serviceWorker, window });

  if (window.__swControllerChangeReloadPending__) {
    window.__swControllerChangeReloadPending__ = false;
    console.info("Service worker update is active. Reloading application");
    reloadApplication(window);
    return;
  }

  const state = store.getState();
  const shouldApplyUpdate = Boolean(state?.ui?.updateNotification?.visible);

  serviceWorker
    .getRegistration()
    .then(reg => {
      if (window.__swReloading__) return;

      if (reg?.waiting) {
        window.__swReloadOnNextControllerChange__ = true;
        dispatchNotification();
        return;
      }

      if (shouldApplyUpdate) {
        reloadApplication(window);
      }
    })
    .catch(() => {
      if (shouldApplyUpdate) {
        reloadApplication(window);
      }
    });
}

/**
 * Runs service-worker navigation tasks and keeps active editor sessions open.
 *
 * Service-worker update checks run on every SPA navigation. Deferred update
 * reloads run when the navigation leaves the editor or opens a fresh editor
 * session from outside the editor.
 */
export function handleServiceWorkerNavigation({
  location,
  previousLocation,
  store,
  serviceWorker,
  window,
  reloadHandler = handleServiceWorkerReload
}: ServiceWorkerNavigationDeps) {
  if (serviceWorker) {
    listenForServiceWorkerControllerChanges({ serviceWorker, window });

    serviceWorker.ready.then(registration => {
      console.info("Checking server for updated service worker");
      registration.update().catch(error => {
        console.info(
          "Service worker update check failed (will retry on next navigation):",
          getErrorMessage(error)
        );
      });
    });

    if (isDeferredReloadSafe(previousLocation?.pathname, location.pathname)) {
      reloadHandler({
        store,
        serviceWorker,
        window
      });
    }
  }

  window.inline_manual_player?.update();
}

/**
 * Creates the history listener that supplies previous and next routes to the
 * service-worker navigation policy.
 */
export function createServiceWorkerNavigationListener({
  initialLocation,
  store,
  serviceWorker,
  window,
  reloadHandler
}: ServiceWorkerNavigationListenerDeps) {
  listenForServiceWorkerControllerChanges({ serviceWorker, window });

  let previousLocation = initialLocation;

  return (location: NavigationLocation) => {
    handleServiceWorkerNavigation({
      location,
      previousLocation,
      store,
      serviceWorker,
      window,
      reloadHandler
    });
    previousLocation = location;
  };
}
